import {
	faCircleCheck,
	faCircleHalfStroke,
	faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Card, Container, Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";
import api from "../api/api";

const ProgramOverview = ({ program, selectedDate, onDateChange }) => {
	const [currentDate, setCurrentDate] = useState(new Date(selectedDate || new Date()));
	const [statusData, setStatusData] = useState({});
	const [loading, setLoading] = useState(true);

	const firstDayOfMonth = new Date(
		currentDate.getFullYear(),
		currentDate.getMonth(),
		1
	);

	const lastDayOfMonth = new Date(
		currentDate.getFullYear(),
		currentDate.getMonth() + 1,
		0
	);

	const daysInMonth = lastDayOfMonth.getDate();

	const startingDayIndex =
		firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;

	useEffect(() => {
		const fetchStatusData = async () => {
			setLoading(true);

			try {
				const monthStr = `${currentDate.getFullYear()}-${String(
					currentDate.getMonth() + 1
				).padStart(2, "0")}`;

				const response = await api.get(
					`/programs/${program.id}/status/?month=${monthStr}`
				);

				setStatusData(response.data || {});
			} catch (error) {
				console.error("Error fetching status data:", error);
				setStatusData({});
			} finally {
				setLoading(false);
			}
		};

		fetchStatusData();
	}, [currentDate, program.id]);

	const previousMonth = () => {
		setCurrentDate(
			new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
		);
	};

	const nextMonth = () => {
		setCurrentDate(
			new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
		);
	};

	const getStatusValue = (dateStr) => {
		const rawStatus = statusData[dateStr];

		if (!rawStatus) {
			return "pending";
		}

		if (typeof rawStatus === "string") {
			return rawStatus;
		}

		if (typeof rawStatus === "object") {
			return rawStatus.status || rawStatus.state || rawStatus.completion_status || "pending";
		}

		return "pending";
	};

	const getStatusClassName = (status) => {
		return String(status)
			.toLowerCase()
			.trim()
			.replace(/\s+/g, "-")
			.replace(/[^a-z0-9-]/g, "");
	};

	const renderStatusIcon = (status) => {
		switch (status) {
			case "completed":
				return (
					<FontAwesomeIcon
						icon={faCircleCheck}
						className="text-success"
						size="1x"
					/>
				);
			case "missed":
				return (
					<FontAwesomeIcon
						icon={faCircleXmark}
						className="text-secondary"
						size="1x"
					/>
				);
			case "in-progress":
				return (
					<FontAwesomeIcon
						icon={faCircleHalfStroke}
						className="text-dark"
						size="1x"
					/>
				);
			default:
				return null;
		}
	};

	const handleDateClick = (date) => {
		onDateChange(date.toLocaleDateString("en-CA"));
	};

	const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

	return (
		<Container fluid className="program-overview p-0">
			<Card className="program-overview-card shadow-sm border-0 overflow-hidden mx-auto">
				<Card.Header className="bg-white py-3 px-3 px-md-4 border-0">
					<Row className="align-items-center">
						<Col xs={12} md={8}>
							<h3 className="m-0 fw-bold fs-4 text-primary">
								{currentDate.toLocaleString("default", {
									month: "long",
									year: "numeric",
								})}
							</h3>
						</Col>

						<Col
							xs={12}
							md={4}
							className="d-flex justify-content-md-end justify-content-center mt-3 mt-md-0"
						>
							<div className="d-flex gap-2">
								<button
									type="button"
									onClick={previousMonth}
									className="program-overview-nav-btn btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm"
								>
									<ChevronLeft size={22} className="text-primary" />
								</button>

								<button
									type="button"
									onClick={nextMonth}
									className="program-overview-nav-btn btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm"
								>
									<ChevronRight size={22} className="text-primary" />
								</button>
							</div>
						</Col>
					</Row>
				</Card.Header>

				<Card.Body className="p-2 p-md-4">
					<div className="program-overview-calendar">
						<div className="program-overview-header-row">
							{weekDays.map((day) => (
								<div key={day} className="program-overview-header-cell">
									<span className="text-muted fw-semibold">{day}</span>
								</div>
							))}
						</div>

						<div className="program-overview-days">
							{Array.from({ length: startingDayIndex }).map((_, index) => (
								<div
									key={`empty-${index}`}
									className="program-overview-day-empty"
								/>
							))}

							{Array.from({ length: daysInMonth }).map((_, index) => {
								const date = new Date(
									currentDate.getFullYear(),
									currentDate.getMonth(),
									index + 1
								);

								const dateStr = date.toLocaleDateString("en-CA");
								const isSelectedDate = selectedDate === dateStr;
								const isToday = dateStr === new Date().toLocaleDateString("en-CA");
								const status = getStatusValue(dateStr);
								const statusClassName = getStatusClassName(status);

								return (
									<button
										type="button"
										key={dateStr}
										onClick={() => handleDateClick(date)}
										className={[
											"program-overview-day-btn",
											isSelectedDate ? "program-overview-selected-date" : "",
											isToday ? "program-overview-today" : "",
											statusClassName ? `program-overview-status-${statusClassName}` : "",
										].join(" ")}
										disabled={loading}
									>
										<span className="program-overview-day-content">
											{status === "not prescribed" || status === "pending" ? (
												<span className="program-overview-day-number">
													{index + 1}
												</span>
											) : (
												<span className="program-overview-status-icon">
													{renderStatusIcon(status)}
												</span>
											)}
										</span>
									</button>
								);
							})}
						</div>
					</div>
				</Card.Body>
			</Card>

			<style>{`
				.program-overview-card {
					max-width: 900px;
				}

				.program-overview-calendar {
					display: grid;
					gap: 1px;
					background-color: #f8f9fa;
					border-radius: 8px;
					overflow: hidden;
				}

				.program-overview-header-row {
					display: grid;
					grid-template-columns: repeat(7, minmax(0, 1fr));
					background-color: #ffffff;
				}

				.program-overview-header-cell {
					display: flex;
					align-items: center;
					justify-content: center;
					min-height: 44px;
					text-align: center;
				}

				.program-overview-days {
					display: grid;
					grid-template-columns: repeat(7, minmax(0, 1fr));
					gap: 1px;
					background-color: #f8f9fa;
				}

				.program-overview-day-empty {
					min-height: 72px;
					background-color: #ffffff;
				}

				.program-overview-day-btn {
					width: 100%;
					min-width: 0;
					min-height: 72px;
					aspect-ratio: auto;
					border: 0;
					border-radius: 0;
					background-color: #ffffff;
					color: inherit;
					cursor: pointer;
					padding: 0;
					appearance: none;
					transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
				}

				.program-overview-day-btn:hover:not(:disabled) {
					background-color: #f8f9fa;
					box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
					transform: translateY(-2px);
				}

				.program-overview-day-btn:disabled {
					cursor: wait;
					opacity: 0.65;
				}

				.program-overview-day-content {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 100%;
					min-height: 72px;
					padding: 0.75rem;
				}

				.program-overview-day-number {
					font-size: 1rem;
					font-weight: 500;
				}

				.program-overview-selected-date {
					background-color: #e7f1ff !important;
					font-weight: 700;
				}

				.program-overview-today {
					box-shadow: inset 0 0 0 1px #0d6efd;
				}

				.program-overview-status-icon svg {
					font-size: 1.35rem;
				}

				.program-overview-nav-btn {
					width: 42px;
					height: 42px;
					transition: transform 0.2s ease;
				}

				.program-overview-nav-btn:hover {
					transform: scale(1.05);
				}

				@media (max-width: 767px) {
					.program-overview-header-cell {
						min-height: 36px;
					}

					.program-overview-day-empty,
					.program-overview-day-btn,
					.program-overview-day-content {
						min-height: 42px;
					}

					.program-overview-day-content {
						padding: 0.35rem;
					}

					.program-overview-day-number {
						font-size: 0.85rem;
					}

					.program-overview-status-icon svg {
						font-size: 1rem;
					}
				}
			`}</style>
		</Container>
	);
};

ProgramOverview.propTypes = {
	onDateChange: PropTypes.func.isRequired,
	program: PropTypes.shape({
		id: PropTypes.number.isRequired,
	}).isRequired,
	selectedDate: PropTypes.string,
};

export default ProgramOverview;